import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../../store';
import { getProjectDocumentSummary } from './sliceProjectSelectedDocuments';
import { IProjectViewData } from '../sliceProjectViews';
import { safeJsonParse } from '../../../util/DataUtils';
import { IFieldConfig } from './sliceProjectDocuments';
import { formatDocumentsToCSV, downloadCSVFile } from '../../../util/ExportToCSVUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsExportToCSVState = {
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetExportToCSVError: (state: Draft<IProjectDocumentsExportToCSVState>) => {
        state.error = null;
    },
};

/**
 * Object of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that export documents to csv.
     * @param config - Object that contains the project database's replicaId, document Ids, currentView, reportName.
     * @returns Promise.
     */
    exportDocumentsToCSV: async (
        config: {
            replicaId: string;
            documentIds: string[];
            currentView: IProjectViewData;
            reportName: string;
        },
        thunkApi,
    ): Promise<void> => {
        const { replicaId, documentIds, currentView, reportName } = config;
        try {
            if (!currentView) {
                return Promise.reject('Failed to export csv');
            }
            const { viewStructure, viewId } = currentView;
            const fieldSchema = safeJsonParse<string[]>(currentView.fieldSchema, null);
            const fields = safeJsonParse<IFieldConfig[]>(currentView.fields, null);

            if (!fieldSchema || !fields) {
                return Promise.reject('Failed to export csv');
            }
            const selectedDocsSummary = await thunkApi.dispatch(
                getProjectDocumentSummary({
                    replicaId,
                    documentIds,
                    viewName: viewStructure,
                    columnName: viewId,
                    fields: [].concat(
                        ...fieldSchema.map(uf => fields.find(x => x.field === uf)?.fields ?? []),
                    ),
                }),
            );

            if (!selectedDocsSummary.payload.documents.length) {
                return Promise.reject('Failed to export csv');
            }

            const CSVData = formatDocumentsToCSV({
                data: selectedDocsSummary.payload.documents,
                fieldSchema,
                fields,
            });

            downloadCSVFile({ CSVData, reportName });

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsExportToCSVState>) => {
        /**
         * Reducers for the 'exportDocumentsToCSV' thunk.
         */
        builder.addCase(
            exportDocumentsToCSV.pending,
            (state: Draft<IProjectDocumentsExportToCSVState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            exportDocumentsToCSV.fulfilled,
            (state: Draft<IProjectDocumentsExportToCSVState>) => {
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            exportDocumentsToCSV.rejected,
            (state: Draft<IProjectDocumentsExportToCSVState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const exportDocumentsToCSV = createAsyncThunk(
    'project/documents/exportToCSV',
    thunks.exportDocumentsToCSV,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocumentsExportToCSVState = SliceProps;

/**
 * Create the redux slice.
 */
export const projectDocumentsExportToCSVSlice = createSlice({
    name: 'projectDocumentsExportToCSV',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { resetExportToCSVError } = projectDocumentsExportToCSVSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsExportToCSVSlice.reducer;
